import {createSlice, type SliceSelectors} from '@reduxjs/toolkit'

import {
    changeSearchDateAction,
    changeSearchKeywordAction,
    initSearch,
    resetSearchKeywordAction,
    selectSearchCategoryAction,
    selectSearchDetailAction,
    unselectSearchCategoryAction,
    unselectSearchDetailAction,
    getInitialState,
} from '@web/slices/search/slice'

import type {MakeCreateSliceOptions, SearchReducers} from './interfaces'
import type {SearchState} from '@web/slices/search/slice'

function getSelectDefault<T extends {type: string; code: string}[]>(selected: string[], items: T): string[] {
    if (!selected.length && items.some((item) => item.type === 'DEFAULT')) {
        return items.filter((item) => item.type === 'DEFAULT').map((item) => item.code)
    }
    return selected
}

export default (options?: MakeCreateSliceOptions) =>
    createSlice<SearchState, SearchReducers, 'search', SliceSelectors<SearchState>>({
        name: 'search',
        initialState: {
            ...getInitialState(),
            ...(options?.initialState ?? {}),
        },
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(initSearch, (state, {payload}) => {
                    state.selected.keyword = payload.keyword ?? ''
                    state.selected.startDate = payload.startDate ?? ''
                    state.selected.endDate = payload.endDate ?? ''
                    const categories = state.category.items
                        .filter(({code}) => payload.categories?.includes(code) ?? false)
                        .map(({code}) => code)
                    state.selected.categories = getSelectDefault(categories, state.category.items)
                    if (state.detail) {
                        const details = state.detail.items
                            .filter(({code}) => payload.details?.includes(code) ?? false)
                            .map(({code}) => code)
                        state.selected.details = getSelectDefault(details, state.detail.items)
                    }
                })
                .addCase(changeSearchKeywordAction, (state, {payload}) => {
                    state.selected.keyword = payload
                })
                .addCase(resetSearchKeywordAction, (state) => {
                    state.selected.keyword = ''
                })
                .addCase(changeSearchDateAction, (state, {payload}) => {
                    state.selected.startDate = payload.startDate
                    state.selected.endDate = payload.endDate
                })
                .addCase(selectSearchCategoryAction, (state, {payload}) => {
                    const {type} = state.category.items.find((item) => item.code === payload.code) ?? {}
                    if (type) {
                        if (type === 'SINGLE' || type === 'DEFAULT') {
                            state.selected.categories = [payload.code]
                        } else {
                            state.selected.categories = [
                                ...state.selected.categories.filter((code) => {
                                    if (code === payload.code) {
                                        return false
                                    }
                                    const item = state.category.items.find((categoryItem) => categoryItem.code === code)
                                    return item && item.type === 'MULTI'
                                }),
                                payload.code,
                            ]
                        }
                    }
                })
                .addCase(unselectSearchCategoryAction, (state, {payload: {code}}) => {
                    const {type} = state.category.items.find((item) => item.code === code) ?? {}
                    if (type) {
                        state.selected.categories = getSelectDefault(
                            type === 'MULTI' ? state.selected.categories.filter((detail) => detail !== code) : [],
                            state.category.items,
                        )
                    }
                })
                .addCase(selectSearchDetailAction, (state, {payload}) => {
                    const detail = state.detail
                    if (detail) {
                        const {type} = detail.items.find((item) => item.code === payload.code) ?? {}
                        if (type) {
                            if (type === 'SINGLE' || type === 'DEFAULT') {
                                state.selected.details = [payload.code]
                            } else {
                                state.selected.details = [
                                    ...state.selected.details.filter((code) => {
                                        if (code === payload.code) {
                                            return false
                                        }
                                        const item = detail.items.find((detailItem) => detailItem.code === code)
                                        return item && item.type === 'MULTI'
                                    }),
                                    payload.code,
                                ]
                            }
                        }
                    }
                })
                .addCase(unselectSearchDetailAction, (state, {payload: {code}}) => {
                    if (state.detail) {
                        const {type} = state.detail.items.find((item) => item.code === code) ?? {}
                        if (type) {
                            state.selected.details = getSelectDefault(
                                type === 'MULTI' ? state.selected.details.filter((detail) => detail !== code) : [],
                                state.detail.items,
                            )
                        }
                    }
                })
            options?.extraReducers?.(builder)
        },
    })
